/**
 * UndoStack - Operation history management for undo/redo functionality
 * 
 * Provides efficient undo/redo operations with memory management
 * and legal document audit trail integration.
 */

import { Operation, InsertOperation, DEFAULT_CONFIG } from '../types/DocumentTypes';

export class UndoStack {
  private undoStack: Operation[] = [];
  private redoStack: Operation[] = [];
  private maxSize: number;

  constructor(maxSize: number = DEFAULT_CONFIG.maxUndoStackSize) {
    this.maxSize = maxSize;
  }

  // ============================================================================
  // CORE OPERATIONS
  // ============================================================================

  /**
   * Add operation to undo stack
   */
  push(operation: Operation): void {
    // Clear redo stack when new operation is added
    this.redoStack = [];
    
    // Add to undo stack
    this.undoStack.push(operation);
    
    // Maintain size limit
    if (this.undoStack.length > this.maxSize) {
      this.undoStack.shift(); // Remove oldest operation
    }
    
    console.log(`[UndoStack] Added operation: ${operation.type} (stack size: ${this.undoStack.length})`);
  }

  /**
   * Undo last operation
   */
  undo(): Operation | null {
    if (this.undoStack.length === 0) {
      return null;
    }
    
    const operation = this.undoStack.pop()!;
    this.redoStack.push(operation);
    
    console.log(`[UndoStack] Undoing operation: ${operation.type} (undo: ${this.undoStack.length}, redo: ${this.redoStack.length})`);
    
    return operation;
  }

  /**
   * Redo last undone operation
   */
  redo(): Operation | null {
    if (this.redoStack.length === 0) {
      return null;
    }
    
    const operation = this.redoStack.pop()!;
    this.undoStack.push(operation);
    
    console.log(`[UndoStack] Redoing operation: ${operation.type} (undo: ${this.undoStack.length}, redo: ${this.redoStack.length})`);
    
    return operation;
  }

  // ============================================================================
  // STACK MANAGEMENT
  // ============================================================================

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Clear all stacks
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    console.log('[UndoStack] Cleared all stacks');
  }

  /**
   * Get stack sizes for UI indicators
   */
  getStackSizes(): { undoCount: number; redoCount: number } {
    return {
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length
    };
  }

  // ============================================================================
  // OPERATION GROUPING
  // ============================================================================

  /**
   * Group operations for smart undo (e.g., word-level undo)
   */
  startOperationGroup(groupId: string): void {
    // Mark beginning of operation group
    const groupMarker: InsertOperation = {
      id: `group-start-${groupId}`,
      type: 'insert',
      version: 0,
      attorney: {} as any,
      timestamp: new Date(),
      position: 0,
      content: ''
    };
    
    this.undoStack.push(groupMarker);
  }

  endOperationGroup(groupId: string): void {
    // Mark end of operation group
    const groupMarker: InsertOperation = {
      id: `group-end-${groupId}`,
      type: 'insert',
      version: 0,
      attorney: {} as any,
      timestamp: new Date(),
      position: 0,
      content: ''
    };
    
    this.undoStack.push(groupMarker);
  }

  // ============================================================================
  // LEGAL AUDIT INTEGRATION
  // ============================================================================

  /**
   * Get operation history for legal audit
   */
  getOperationHistory(): Operation[] {
    return [...this.undoStack]; // Return copy for safety
  }

  /**
   * Export operation history for legal compliance
   */
  exportAuditTrail(): string {
    const auditData = {
      exportDate: new Date(),
      totalOperations: this.undoStack.length,
      operations: this.undoStack.map(op => ({
        id: op.id,
        type: op.type,
        timestamp: op.timestamp,
        attorney: {
          name: op.attorney.name,
          barNumber: op.attorney.barNumber
        },
        position: op.position,
        contentLength: op.type === 'insert' ? op.content?.length : 
                      op.type === 'delete' ? op.deletedContent?.length : 0
      }))
    };
    
    return JSON.stringify(auditData, null, 2);
  }

  // ============================================================================
  // MEMORY MANAGEMENT
  // ============================================================================

  /**
   * Optimize memory usage by compacting old operations
   */
  compactHistory(): void {
    if (this.undoStack.length <= this.maxSize * 0.8) {
      return; // No need to compact
    }
    
    console.log(`[UndoStack] Compacting history (current size: ${this.undoStack.length})`);
    
    // Keep only recent operations
    const keepCount = Math.floor(this.maxSize * 0.6);
    const removedCount = this.undoStack.length - keepCount;
    
    this.undoStack = this.undoStack.slice(-keepCount);
    
    console.log(`[UndoStack] Compacted: removed ${removedCount} operations, kept ${keepCount}`);
  }

  /**
   * Get memory usage estimate
   */
  getMemoryUsage(): number {
    // Estimate memory usage of stored operations
    let totalSize = 0;
    
    for (const operation of this.undoStack) {
      totalSize += this.estimateOperationSize(operation);
    }
    
    for (const operation of this.redoStack) {
      totalSize += this.estimateOperationSize(operation);
    }
    
    return totalSize;
  }

  private estimateOperationSize(operation: Operation): number {
    // Rough estimate of operation memory footprint
    let size = 200; // Base object overhead
    
    if (operation.type === 'insert') {
      size += operation.content?.length * 2 || 0; // UTF-16 characters
    } else if (operation.type === 'delete') {
      size += operation.deletedContent?.length * 2 || 0;
    }
    
    return size;
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  /**
   * Validate stack consistency
   */
  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Check stack sizes
    if (this.undoStack.length > this.maxSize) {
      errors.push(`Undo stack exceeds max size: ${this.undoStack.length} > ${this.maxSize}`);
    }
    
    // Check operation consistency
    for (let i = 1; i < this.undoStack.length; i++) {
      const prev = this.undoStack[i - 1];
      const curr = this.undoStack[i];
      
      if (curr.version <= prev.version) {
        errors.push(`Version inconsistency at index ${i}: ${curr.version} <= ${prev.version}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
